/**
 * House-rules layer — filters declarations and opening bids from GameConfig.houseRules.
 */

import type { Layer } from './types';
import type { GameState } from '../types';
import type { HouseRulesConfig } from '../types/config';
import { BID_TYPES } from '../constants';

/** Pre-collapse configs carried paired high/low flags. */
type LegacyHouseRulesConfig = HouseRulesConfig & {
  allowFollowMeHigh?: boolean;
  allowFollowMeLow?: boolean;
};

function readHouseRules(state: GameState): HouseRulesConfig | undefined {
  const raw = state.initialConfig.houseRules as LegacyHouseRulesConfig | undefined;
  if (!raw) {
    return undefined;
  }
  return {
    ...raw,
    // Old matches replayed from storage or share URLs may only carry the
    // paired flags; their effective rule was "allowed if either flag is on".
    allowFollowMe:
      raw.allowFollowMe ??
      (raw.allowFollowMeHigh !== false || raw.allowFollowMeLow !== false),
  };
}

function trumpAllowedByHouseRules(
  houseRules: HouseRulesConfig,
  trumpType: string | undefined,
): boolean {
  if (trumpType === 'doubles') {
    return houseRules.allowDoublesTrump !== false;
  }
  if (trumpType === 'no-trump') {
    return houseRules.allowFollowMe !== false;
  }
  return true;
}

export const houseRulesLayer: Layer = {
  name: 'houseRules',

  getValidActions: (state, prev) => {
    const houseRules = readHouseRules(state);
    if (!houseRules) {
      return prev;
    }

    let actions = prev;

    if (state.phase === 'trump_selection') {
      actions = actions.filter((action) => {
        if (action.type !== 'select-trump') {
          return true;
        }
        return trumpAllowedByHouseRules(houseRules, action.trump?.type);
      });
    }

    if (state.phase === 'bidding' && houseRules.openingBidCapMarks != null) {
      const cap = houseRules.openingBidCapMarks;
      const previousBids = state.bids.filter((entry) => entry.type !== BID_TYPES.PASS);
      if (previousBids.length === 0) {
        actions = actions.filter((action) => {
          if (action.type !== 'bid' || action.bid !== BID_TYPES.MARKS) {
            return true;
          }
          if (action.value === undefined) {
            return true;
          }
          return action.value >= 1 && action.value <= cap;
        });
      }
    }

    return actions;
  },

  rules: {
    isValidBid: (state, bid, _playerHand, prev) => {
      const houseRules = readHouseRules(state);
      if (!prev || !houseRules?.openingBidCapMarks) {
        return prev;
      }

      const previousBids = state.bids.filter((entry) => entry.type !== BID_TYPES.PASS);
      if (previousBids.length > 0 || bid.type !== BID_TYPES.MARKS) {
        return prev;
      }

      const cap = houseRules.openingBidCapMarks;
      if (cap === null || bid.value === undefined) {
        return prev;
      }

      return bid.value >= 1 && bid.value <= cap;
    },

    isValidTrump: (state, trump, prev) => {
      if (!prev) {
        return prev;
      }
      const houseRules = readHouseRules(state);
      if (!houseRules) {
        return prev;
      }
      return trumpAllowedByHouseRules(houseRules, trump.type);
    },
  },
};
